import { Environment } from "@common/models/Enums";
import { getQueryParams, getRandomWaitInterval, http } from "@common/utils";
import { action, computed, makeObservable, observable } from "mobx";

export const enum HubConnectionState {
    Connecting = 0,
    Connected = 1,
    Reconnecting = 2,
    Disconnected = 4
}

export type QueuedSignalRMethod<T> = {
    methodName: string,
    args: any[],
    resolve?: (value: T | PromiseLike<T>) => void,
    reject?: (reason: any) => void
}

export type BaseHubMethods = {
    [key: symbol | string]: (...args: any[]) => any;

    // TODO - MS: This should be moved to the HospitalHub
    "joinVisitGroup": (visitId: number, groupType: HubGroupType) => boolean;
    "leaveVisitGroup": (visitId: number, groupType: HubGroupType) => boolean;
    "OtherJoinedVisit": (connectionId: string, visitId: number, groupType: HubGroupType) => void;
    "OtherLeftVisit": (connectionId: string, visitId: number, groupType: HubGroupType) => void;
}

export type BaseHubClientMethod =
    { eventName: "onJoinedVisitGroup", callback: (connectionId: string, visitId: number, groupType: HubGroupType, userName: string) => void } |
    { eventName: "onLeftVisitGroup", callback: (connectionId: string, visitId: number, groupType: HubGroupType, userName: string) => void } |
    { eventName: "onOtherJoinedVisitGroup", callback: (connectionId: string, visitId: number, groupType: HubGroupType, userName: string) => void } |
    { eventName: "onOtherLeftVisitGroup", callback: (connectionId: string, visitId: number, groupType: HubGroupType, userName: string) => void } |
    { eventName: "onOtherConnectionDisconnected", callback: (connectionId: string, userName: string) => void } |
    { eventName: "onVisitUpdated", callback: (connectionId: string, visitId: number) => void }

export type BaseHubInitOptions = {
    hubName: string,
    isMock?: boolean,
    env: Environment
}

export enum HubGroupType {
    None = 0,
    LoggedIntoHospital = 100,
    VisitExamOpen = 200
}
export abstract class BaseHub {
    private _pingHandle: NodeJS.Timer | undefined = undefined;
    private _invokeQueue: QueuedSignalRMethod<any>[] = [];
    private _manualStop: boolean = false;
    private _loggedOut: boolean = false;
    private _isMock: boolean = false;
    private _isLocalDev: boolean = false;
    private _isAlpha: boolean = false;

    @observable private _connection: SignalR.Hub.Connection | undefined;
    @observable private _hub: SignalR.Hub.Proxy | undefined;
    @observable private _prevConnectionState: HubConnectionState = HubConnectionState.Disconnected;
    @observable private _connectionState: HubConnectionState = HubConnectionState.Disconnected;



    protected constructor(options: BaseHubInitOptions) {
        const me = this;
        this._isMock = Boolean(options.isMock);
        this._isLocalDev = options.env === Environment.local;
        this._isAlpha = options.env === Environment.alpha;
        const queryParams = this._isMock ? {} : getQueryParams();
        if (jQuery.hubConnection) {
            this._connection = jQuery.hubConnection("/azsignalr", {
                useDefaultPath: false
            });
            if (queryParams["enableLogging"] || this._isLocalDev || this._isAlpha)
                this._connection.logging = true;

            this._hub = this._connection.createHubProxy(options.hubName);

            //If you implement any client methods in IBaseWSHub server side, you can subscribe to them here
            //i.e. this._hub.on('newBaseServerSideMethod', ...);

            this._connection.error(function (this: SignalR.Connection) {
                me.onError.call(me, this);
            });
            //register signal r state methods
            this._connection.starting(function (this: SignalR.Connection) {
                me.onStarting.call(me, this);
            });
            //not to be confused with data state, this is connection state.  AzureSignalR on Asp.Net does not support data state.
            this._connection.stateChanged(function (this: SignalR.Connection, state: SignalR.StateChanged) {
                me.onStateChanged.call(me, this, state);
            });
            this._connection.reconnecting(function (this: SignalR.Connection) {
                me.onConnectionReconnecting.call(me, this);
            });
            this._connection.reconnected(function (this: SignalR.Connection) {
                me.onConnectionReconnected.call(me, this);
            });
            this._connection.disconnected(function (this: SignalR.Connection) {
                me.onConnectionDisconnected.call(me, this);
            });

            this._hub.on("onPingEcho", () => {
            });
        }

        makeObservable(this, undefined, { autoBind: true });
        (globalThis as unknown as any)[options.hubName] = this;
    }

    //#region Base Hub Invokable Methods
    public joinVisitGroup = async (visitId: number, groupType: HubGroupType) => {
        return await this.safeInvoke<BaseHubMethods, "joinVisitGroup", boolean>("joinVisitGroup", visitId, groupType);
    }

    public leaveVisitGroup = async (visitId: number, groupType: HubGroupType) => {
        return await this.safeInvoke<BaseHubMethods, "leaveVisitGroup", boolean>("leaveVisitGroup", visitId, groupType);
    };
    //#endregion

    //#region ----------------------------------------------------
    /**
     * The ID of the current signalR connection if there is one connected
     */
    @computed get connectionId() {
        return this._connection ? this._connection.id : '';
    }

    @computed get connectionState() {
        return this._hub ? this._hub.connection.state : HubConnectionState.Disconnected;
    }

    @action.bound protected onError(this: BaseHub, connection: SignalR.Connection) {
    }

    @action.bound protected initiatePing() {
        if (this._pingHandle)
            clearInterval(this._pingHandle);
        this._pingHandle = setInterval(() => {
            if (this._connectionState === HubConnectionState.Connected)
                this._hub?.invoke('ping', true);
        }, 600000); //every 10 minutes
    }

    @action.bound protected stopPing() {
        if (this._pingHandle)
            clearInterval(this._pingHandle);
    }

    /**
     * Overridable, is fired when the connection is starting and before it finishes connecting
     * @param this 
     * @param connection 
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    @action.bound protected onStarting(this: BaseHub, connection: SignalR.Connection) {
    }

    /**
     * Overridable, is fired when the connection has connected and ConnectionState is in state 1 (Connected)
     */
    @action.bound protected onConnected(): void {
    }

    @action.bound startConnection() {
        this._manualStop = false;
        this.withConnection(conn => {
            if (conn.state !== HubConnectionState.Disconnected)
                return; //already connected or already reconnecting or connecting
            conn.start({
                jsonp: false,
                withCredentials: true,
                transport: ['webSockets'],
                pingInterval: undefined //using our own ping in the BaseWSHub so it counts as Azure Traffic and keeps the token alive.
            }).then(() => {
                this.initiatePing();
            });
        });
    }

    @action.bound setLoggedOut(isLoggedOut: boolean) {
        this._loggedOut = isLoggedOut;
    }

    @action.bound stopConnection(opts?: { ignoreManual?: boolean }) {
        if (opts?.ignoreManual)
            this._manualStop = true;

        this._connection?.stop();
    }

    /**
     * This calls the hubs (on) method to register callback events triggered from the signalr server to this connected client
     * And provides some amount of type safety if you pass in a string literal union as <M>
     * @param methodName 
     * @param callback 
     */
    @action.bound public registerMethod<T extends { eventName: any, callback: (...args: any[]) => void }>(event: T) {
        this.withHub(hub => {
            hub.on(event.eventName as string, event.callback);
        });
    }

    /**
     * This calls the hubs (off) method to unregister callback events so they are cleaned up and no longer triggered by the signalr server to this connected client.
     * @param event 
     */
    @action.bound public unregisterMethod<T extends { eventName: any; callback: (...agrs: any[]) => void; }>(event: T) {
        this.withHub(hub => {
            hub.off(event.eventName as string, event.callback);
        });
    }

    /**
     * Must Override/Implement:
     * CleanQue is to allow the implementing class to remove things from the queue before they are processed.  This is useful
     * if you might have 5 things in the que and 4 are redundant so you remove the first four and keep the fith in the cleanQue method.
     * The array you return should be the cleaned array.
     * @param queue 
     */
    protected cleanQue(queue: QueuedSignalRMethod<any>[]): QueuedSignalRMethod<any>[] {
        const lastIndex = queue.length - 1;
        const lastItem = queue[lastIndex];
        let newQueue: QueuedSignalRMethod<any>[] = queue;
        let droppedItems: QueuedSignalRMethod<any>[] = [];
        let dropped = false;
        function processDrop(name: string) {
            if (dropped)
                return;
            newQueue = queue.filter((a, index) => {
                if (a.methodName === name && index < lastIndex) {
                    droppedItems.push(a);
                    if (!dropped)
                        dropped = false;
                    return false;
                }
                return true;
            });
        }

        //check if the current lastmethod is joinVisitGroup, if so drop any previous joinVisitGroup calls (can only be in one visit at a time)
        if (lastItem.methodName === "joinVisitGroup")
            processDrop(lastItem.methodName);
        //check if derrived classes have any extra functions to drop previous calls to from the queue
        _.some(this.getExtraQueueMethodsToClean(), (name) => { processDrop(name); return dropped; });

        //the dropped checks above are because if the last item has already processed some drops there's no sense in comparing any other drops because they won't match and wastes processing time for no reason.

        //resolve any dropped methods (todo: might be better to reject/cancel these)
        droppedItems.forEach((v) => {
            if (v.resolve)
                v.resolve(undefined);
        });

        return newQueue;
    }

    protected getExtraQueueMethodsToClean(): string[] {
        return [];
    }

    /**
     * Overridable, This will let you call invoke whether the connection is connected or not, the connection is unblocking and this will queue invokes if the connection is down and will process the queue when it's reconnected.
     * @param methodName 
     * @param args 
     * @returns 
     */
    protected safeInvoke = async <M extends BaseHubMethods, MK extends keyof M, T>(methodName: MK, ...args: Parameters<M[keyof Pick<M, MK>]>) => {
        const method: QueuedSignalRMethod<T | undefined | null> = {
            methodName: methodName as unknown as string, args
        }
        const retPromise = new Promise<T | undefined>((resolve, reject) => {
            method.resolve = action((value: any) => {
                resolve(value);
            });
            method.reject = action((err: any) => {
                reject(err);
            });
        })

        if (!this._isMock) {
            this._invokeQueue.push(method);
            this._invokeQueue = this.cleanQue(this._invokeQueue);
            this.processQueue();
        } else {
            if (method.resolve)
                method.resolve(undefined);
        }

        return await retPromise;
    };

    //#region Private/Internal methods
    /**
     * Fired when the signalr client connection state changes.
     * @param this 
     * @param connection 
     * @param state 
     */
    @action.bound private onStateChanged(this: BaseHub, connection: SignalR.Connection, state: SignalR.StateChanged) {
        this.withHub(hub => {
            switch (state.newState) {
                case HubConnectionState.Connected:
                    http.updateSignalRConnection(hub.hubName, this.connectionId);
                    this.onConnected();
                    this.processQueue(); //<- newly connected, process the invoke queue
                    break;
                case HubConnectionState.Connecting:
                case HubConnectionState.Disconnected:
                    http.updateSignalRConnection(hub.hubName, null);
                    break;
                case HubConnectionState.Reconnecting:
                default:
                    break;
            }
            this._prevConnectionState = state.oldState;
            this._connectionState = state.newState;
        })
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    @action.bound private onConnectionReconnecting(this: BaseHub, connection: SignalR.Connection) {
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    @action.bound private onConnectionReconnected(this: BaseHub, connection: SignalR.Connection) {
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    @action.bound private onConnectionDisconnected(this: BaseHub, connection: SignalR.Connection) {
        const intervalWait = getRandomWaitInterval(2000, 10000);
        if (!this._manualStop && !this._loggedOut)
            setTimeout(() => {
                this.startConnection.call(this); //<- will call start connection on repeat, if it stops it'll fire disconnected again where this will call start again
            }, intervalWait); //<- wait randomly between 2 seconds and 10 seconds
    }

    /**
     * Server Side methods are queued up in array FIFO style, this processes them all in order they were received
     * This prevents trying to make signalr calls if disconnected without having connections block the UI.
     * @returns 
     */
    @action private processQueue() {
        const internalProcessQueue = (ldone: () => void, lReject: (err: any) => void) => {
            if (!this._connection || this._connection.state !== HubConnectionState.Connected) {
                return;
            }

            if (this._invokeQueue.length <= 0) {
                ldone();
                return;
            }
            const method = this._invokeQueue[0];
            if (!method) {
                this._invokeQueue.shift();
                return;
            }
            this.withHub(hub => {
                hub.invoke(method.methodName, ...method.args).then((value: any) => {
                    if (method.resolve)
                        method.resolve(value);
                }).fail((err) => {
                    if (method.reject)
                        method.reject(err);
                }).always(() => {
                    this._invokeQueue.shift();
                    internalProcessQueue(ldone, lReject);
                });

            })
        }

        let done: () => void;
        let doneReject: (err: any) => void;
        const promise = new Promise<void>((resolve, reject) => {
            done = resolve;
            doneReject = reject;
            internalProcessQueue(done, doneReject);
        }).catch((err) => {
            doneReject(err);
        });

        return promise;
    }

    @action.bound protected withConnection(cb: (connection: SignalR.Connection) => void) {
        if (this._connection)
            cb(this._connection);
    }

    @action.bound protected withHub(cb: (hub: SignalR.Hub.Proxy) => void) {
        if (this._hub)
            cb(this._hub);
    }
    //#endregion
    //#endregion
}
